/**
 * PATH: packages/email-hub/src/feature/components/rich-text/signatureBlockNode.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-SIGBLOCKNODE-001
 * PURPOSE: A TipTap atom node that embeds a real email signature (a layout
 *          table copied from Outlook/Apple Mail) as an OPAQUE block. The
 *          editor never descends into it, so its banner/logo/colspans survive
 *          byte-for-byte; the NodeView renders the (sanitised) signature so the
 *          operator sees an exact, WYSIWYG preview.
 * WHY:     prosemirror-tables normalises any pasted layout table into a
 *          rectangular grid — shattering the signature (BUG-2026-06-29-EMAIL-
 *          SIGNATURE-LAYOUT-MANGLED). Carrying the signature as an opaque atom
 *          (same trick as the Kanban-card chip) is the only faithful path.
 * FLOW:
 *   <div data-qte-sig="enc"> ─parseHTML▶ node{html} ─NodeView▶ sanitised preview
 *   getHTML ─renderHTML▶ <div data-qte-sig="enc">  (round-trips drafts verbatim)
 *   send: emailHtml.toEmailHtml expands the block to its raw signature HTML.
 * RELATED:
 *   - signatureBlock.ts: encode/decode/expand pure helpers
 *   - kanbanCardNode.tsx: the atom-node pattern this mirrors
 */

import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import type { NodeViewProps } from '@tiptap/react'
import { X } from 'lucide-react'
import {
  SIGNATURE_BLOCK_ATTR,
  encodeSignatureBlock,
  decodeSignatureBlock,
} from './signatureBlock'
import { sanitizeEmailHtml } from './emailHtml'

function SignatureBlockView({ node, deleteNode, editor }: NodeViewProps) {
  const raw = (node.attrs.html as string) || ''
  // Defence-in-depth: the value is sanitised on save AND again here before it
  // ever reaches innerHTML, so a hand-edited draft can't smuggle script in.
  const safe = sanitizeEmailHtml(raw)
  return (
    <NodeViewWrapper
      data-qte-signature-view=""
      className="group relative my-2 rounded-md border border-dashed border-border/70 bg-muted/20 p-2"
    >
      {editor?.isEditable && (
        <div className="pointer-events-none absolute -top-2 left-2 select-none rounded bg-background px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Signature
        </div>
      )}
      {editor?.isEditable && (
        <button
          type="button"
          contentEditable={false}
          onClick={() => deleteNode()}
          className="absolute -top-2 right-2 hidden rounded bg-background p-0.5 text-muted-foreground shadow-sm hover:text-destructive group-hover:block"
          aria-label="Remove signature"
          title="Remove signature"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {/* The signature is opaque to ProseMirror — render it verbatim. */}
      <div
        className="qte-signature-preview"
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    </NodeViewWrapper>
  )
}

export const SignatureBlockNode = Node.create({
  name: 'signatureBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      html: {
        default: '',
        parseHTML: (el) => decodeSignatureBlock(el.getAttribute(SIGNATURE_BLOCK_ATTR)),
        renderHTML: (attrs) => ({
          [SIGNATURE_BLOCK_ATTR]: encodeSignatureBlock((attrs.html as string) || ''),
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: `div[${SIGNATURE_BLOCK_ATTR}]` }]
  },

  renderHTML({ HTMLAttributes }) {
    // Canonical serialisation kept EMPTY (the html lives in the attribute) so
    // the editor never re-parses the layout table. emailHtml.toEmailHtml
    // expands it to the raw signature on send.
    return ['div', mergeAttributes(HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(SignatureBlockView)
  },
})
